import { getAsbestosFacilityById } from "@/views/asbestosFacility/asbestosFacilityApi";
import { getConstructionProjectById } from "@/views/constructionProject/constructionProjectApi";
import { getDustClassById } from "@/views/dustClass/dustClassApi";
import { getDustMonitorClassById } from "@/views/dustMonitorClass/dustMonitorClassApi";
import { getPermitById } from "@/views/permit/permitApi";
import { getStationarySourceById } from "@/views/stationarySource/stationarySourceApi";
import { routeNames } from "@/routes";
import type { PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { getMurnParts, getMurnString } from "@modernary/mui/utils/helpers";
import type { AxiosResponse } from "axios";
import type { RouteLocationRaw } from "vue-router";

type EntityFetcher = (id: string | number, config?: PagingApiAndNotifierConfig) => Promise<AxiosResponse<any>>;

interface EntityRegistryEntry {
  fetchById: EntityFetcher;
  viewRouteName: string;
}

let registry: Record<string, EntityRegistryEntry> | undefined;

// Built on first use: `@/routes` imports the views that import this service, so reading
// routeNames while this module evaluates would hit the circular-import temporal dead zone.
function getRegistry(): Record<string, EntityRegistryEntry> {
  if (!registry) {
    registry = {
      permit: { fetchById: getPermitById, viewRouteName: routeNames.permit.view },
      constructionProject: {
        fetchById: getConstructionProjectById,
        viewRouteName: routeNames.constructionProject.view,
      },
      stationarySource: {
        fetchById: getStationarySourceById,
        viewRouteName: routeNames.stationarySource.view,
      },
      asbestosFacility: {
        fetchById: getAsbestosFacilityById,
        viewRouteName: routeNames.asbestosFacility.view,
      },
      dustClass: {
        fetchById: getDustClassById,
        viewRouteName: routeNames.dustClass.view,
      },
      dustMonitorClass: {
        fetchById: getDustMonitorClassById,
        viewRouteName: routeNames.dustMonitorClass.view,
      },
    };
  }
  return registry;
}

function getRegistryEntry(resourcePath: string | undefined): EntityRegistryEntry | undefined {
  return resourcePath ? getRegistry()[resourcePath] : undefined;
}

export function buildEntityMurn(resourcePath: string, id: string | number) {
  return getMurnString(resourcePath, id);
}

export function getEntityViewLocation(murn: string | undefined | null): RouteLocationRaw | undefined {
  if (!murn) return undefined;
  const { resourcePath, id } = getMurnParts(murn);
  const entry = getRegistryEntry(resourcePath);
  if (!entry || id == null || id === "") return undefined;
  return { name: entry.viewRouteName, params: { id } };
}

export function fetchEntityByMurn(murn: string, config: PagingApiAndNotifierConfig = {}) {
  const { resourcePath, id } = getMurnParts(murn);
  const entry = getRegistryEntry(resourcePath);
  if (!entry || id == null || id === "") {
    return Promise.reject(new Error(`Unsupported or invalid entity MURN: ${murn}`));
  }
  return entry.fetchById(id, config);
}

export function formatParentEntityLabel(murn: string, entity: Record<string, any> | null | undefined): string {
  const { resourcePath, id } = getMurnParts(murn);
  if (!entity) return id ?? murn;

  switch (resourcePath) {
    case "constructionProject":
      return [entity.name, entity.number].filter(Boolean).join(" - ") || `Construction Project ${id}`;
    case "permit": {
      const project = entity.constructionProject;
      if (project?.name || project?.number) {
        return [project.name, project.number].filter(Boolean).join(" - ");
      }
      return `Permit No. ${id}`;
    }
    case "stationarySource":
      return [entity.name, entity.number].filter(Boolean).join(" - ") || `Stationary Source ${id}`;
    case "asbestosFacility":
      return entity.name || `Asbestos Facility ${id}`;
    case "dustClass":
      return `Dust Class No. ${id}`;
    case "dustMonitorClass":
      return `Dust Monitor Class No. ${id}`;
    default:
      return id ?? murn;
  }
}
